import type { IStatBucket, IStatusSet } from '@platform/core/types';
import type { IAdviceInput } from './IAdviceInput';
import type { IAdviceItem } from './IAdviceItem';

/** Відра статистики `by=status` лише з відкритими статусами (без закритих за визначенням і без порожнього значення). */
export function openBucketsOf(buckets: readonly IStatBucket[], statuses: IStatusSet): IStatBucket[] {
  const closed = new Set(statuses.list.filter((s) => s.closed).map((s) => s.value));
  return buckets.filter((b) => b.value !== null && !closed.has(b.value) && b.count > 0);
}

/**
 * Статуси, що ділять першість «вузького місця» (прототип: відкритих більше двох і на етапі щонайменше половина): кілька — лише коли два
 * етапи мають рівно по половині відкритої роботи; тоді прототип бере етап, що першим трапляється в масиві записів (за номером). Чиста функція.
 */
export function tiedBottlenecksOf(open: number, buckets: readonly IStatBucket[]): string[] {
  const max = Math.max(0, ...buckets.map((b) => b.count));
  if (open <= 2 || !max || max / open < 0.5) return [];
  return buckets.filter((b) => b.count === max && b.value !== null).map((b) => b.value as string);
}

/**
 * Поради модуля за правилами прототипу v4 (ovVals) для кожного модуля, реєстри теж: прострочені — висока важливість; без виконавця та без
 * змін два тижні — середня; коли відкритих записів більше двох і щонайменше половина з них на одному етапі — «вузьке місце» (низька);
 * далі для реєстру ризиків — відкриті критичні ризики (висока) і високі ризики, прийняті без зниження (середня). Без жодної поради —
 * спокійне «нічого не потребує уваги». Порядок — як у прототипі; огляд показує перші три. Чиста функція.
 */
export function adviceOf(input: IAdviceInput): IAdviceItem[] {
  const out: IAdviceItem[] = [];
  if (input.overdue) out.push({ key: 'overdue', level: 'high', count: input.overdue });
  if (input.unassigned) out.push({ key: 'unassigned', level: 'mid', count: input.unassigned });
  if (input.stale) out.push({ key: 'stale', level: 'mid', count: input.stale });
  const tied = tiedBottlenecksOf(input.open, input.openByStatus);
  const stage = tied.find((s) => s === input.firstStatus) ?? tied[0];
  if (stage) out.push({ key: 'bottleneck', level: 'low', stage });
  if (input.critical) out.push({ key: 'critical', level: 'high', count: input.critical });
  if (input.accepted) out.push({ key: 'accepted', level: 'mid', count: input.accepted });
  if (!out.length) out.push({ key: 'none', level: 'low' });
  return out;
}
